import React, { useEffect, useMemo, useState } from 'react';
import clinicalTemplateAdminService from './clinicalTemplateAdminService.js';

const EMPTY_FORM = {
  bodyPartTemplateId: '',
  palpationCode: '',
  displayName: '',
  sideSpecific: false,
  templateLaterality: '',
  required: true,
  displayOrder: 0,
  active: true,
  adminNotes: '',
};

function normalizeTemplateToForm(template) {
  return {
    bodyPartTemplateId:
      template?.bodyPartTemplateId === null ||
      template?.bodyPartTemplateId === undefined
        ? ''
        : String(template.bodyPartTemplateId),
    palpationCode: template?.palpationCode || '',
    displayName: template?.displayName || '',
    sideSpecific: Boolean(template?.sideSpecific),
    templateLaterality: template?.templateLaterality || '',
    required: Boolean(template?.required),
    displayOrder:
      template?.displayOrder === null || template?.displayOrder === undefined
        ? 0
        : Number(template.displayOrder),
    active: Boolean(template?.active),
    adminNotes: template?.adminNotes || '',
  };
}

function formatDateTime(value) {
  if (!value) {
    return 'N/A';
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return parsed.toLocaleString();
}

function validateNonNegativeInteger(value, message) {
  if (value === null || value === undefined || String(value).trim() === '') {
    return message;
  }

  const numericValue = Number(value);

  if (!Number.isInteger(numericValue) || numericValue < 0) {
    return message;
  }

  return '';
}

function validateForm(form, bodyPartMap) {
  const errors = {};
  const bodyPartTemplateId = Number(form.bodyPartTemplateId);
  const bodyPart = bodyPartMap.get(bodyPartTemplateId);
  const palpationCode = String(form.palpationCode || '').trim();
  const displayName = String(form.displayName || '').trim();
  const adminNotes = String(form.adminNotes || '').trim();

  if (!Number.isInteger(bodyPartTemplateId) || bodyPartTemplateId <= 0) {
    errors.bodyPartTemplateId = 'Select a body part.';
  }

  if (!palpationCode) {
    errors.palpationCode = 'Palpation code is required.';
  } else if (palpationCode.length > 120) {
    errors.palpationCode = 'Palpation code cannot exceed 120 characters.';
  } else if (!/^[A-Za-z0-9_-]+$/.test(palpationCode)) {
    errors.palpationCode =
      'Palpation code may contain only letters, numbers, underscores, and hyphens.';
  }

  if (!displayName) {
    errors.displayName = 'Palpation display name is required.';
  } else if (displayName.length > 150) {
    errors.displayName =
      'Palpation display name cannot exceed 150 characters.';
  }

  if (form.sideSpecific && bodyPart && !bodyPart.supportsLaterality) {
    errors.sideSpecific =
      'This body part does not support laterality, so the palpation item cannot be side-specific.';
  }

  if (
    form.sideSpecific &&
    !['LEFT', 'RIGHT'].includes(String(form.templateLaterality || ''))
  ) {
    errors.templateLaterality = 'Choose Left or Right for a side-specific template.';
  }

  const displayOrderError = validateNonNegativeInteger(
    form.displayOrder,
    'Display order must be a whole number of 0 or greater.'
  );

  if (displayOrderError) {
    errors.displayOrder = displayOrderError;
  }

  if (adminNotes.length > 500) {
    errors.adminNotes = 'Admin notes cannot exceed 500 characters.';
  }

  if (form.active && bodyPart && !bodyPart.active) {
    errors.active =
      'An active palpation template cannot belong to an inactive body part.';
  }

  return errors;
}

function buildRequest(form) {
  return {
    bodyPartTemplateId: Number(form.bodyPartTemplateId),
    palpationCode: String(form.palpationCode || '').trim().toUpperCase(),
    displayName: String(form.displayName || '').trim(),
    sideSpecific: Boolean(form.sideSpecific),
    templateLaterality: form.sideSpecific ? form.templateLaterality : null,
    required: Boolean(form.required),
    displayOrder: Number(form.displayOrder),
    active: Boolean(form.active),
    adminNotes: String(form.adminNotes || '').trim() || null,
  };
}

export default function PalpationTemplateAdminSection() {
  const [templates, setTemplates] = useState([]);
  const [bodyParts, setBodyParts] = useState([]);
  const [selectedBodyPartId, setSelectedBodyPartId] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeActionId, setActiveActionId] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formErrors, setFormErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const isEditing = Boolean(editingTemplate?.palpationTemplateId);

  const bodyPartMap = useMemo(() => {
    const map = new Map();

    bodyParts.forEach((bodyPart) => {
      map.set(Number(bodyPart.bodyPartTemplateId), bodyPart);
    });

    return map;
  }, [bodyParts]);

  const selectedBodyPart = useMemo(
    () => bodyPartMap.get(Number(selectedBodyPartId)) || null,
    [bodyPartMap, selectedBodyPartId]
  );

  const sortedTemplates = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return [...templates]
      .filter((template) => {
        if (statusFilter === 'ACTIVE' && !template.active) {
          return false;
        }

        if (statusFilter === 'INACTIVE' && template.active) {
          return false;
        }

        if (!normalizedSearch) {
          return true;
        }

        return [
          template.palpationTemplateId,
          template.palpationCode,
          template.displayName,
          template.templateLaterality,
          template.adminNotes,
        ]
          .filter((value) => value !== null && value !== undefined)
          .join(' ')
          .toLowerCase()
          .includes(normalizedSearch);
      })
      .sort((first, second) => {
        const firstOrder = Number(first.displayOrder ?? 0);
        const secondOrder = Number(second.displayOrder ?? 0);

        if (firstOrder !== secondOrder) {
          return firstOrder - secondOrder;
        }

        return String(first.displayName || '').localeCompare(
          String(second.displayName || '')
        );
      });
  }, [templates, searchTerm, statusFilter]);

  const activeCount = useMemo(
    () => templates.filter((template) => template.active).length,
    [templates]
  );

  useEffect(() => {
    loadBodyParts();
  }, []);

  useEffect(() => {
    if (selectedBodyPartId) {
      loadTemplates(selectedBodyPartId);
    } else {
      setTemplates([]);
      setLoading(false);
    }
  }, [selectedBodyPartId]);

  const showSuccess = (message) => {
    setSuccess(message);
    window.setTimeout(() => setSuccess(''), 3500);
  };

  const loadBodyParts = async () => {
    try {
      setLoading(true);
      setLoadError('');

      const bodyPartData =
        await clinicalTemplateAdminService.getAllBodyPartTemplates();
      const normalized = Array.isArray(bodyPartData) ? bodyPartData : [];

      setBodyParts(normalized);

      if (normalized.length > 0) {
        const firstActive = normalized.find((bodyPart) => bodyPart.active);
        const initial = firstActive || normalized[0];

        setSelectedBodyPartId((current) =>
          current || String(initial.bodyPartTemplateId)
        );

        setForm((current) => ({
          ...current,
          bodyPartTemplateId:
            current.bodyPartTemplateId || String(initial.bodyPartTemplateId),
        }));
      } else {
        setLoading(false);
      }
    } catch (error) {
      setBodyParts([]);
      setTemplates([]);
      setLoadError(
        error?.message || 'Body-part configuration could not be loaded.'
      );
      setLoading(false);
    }
  };

  const loadTemplates = async (bodyPartTemplateId) => {
    try {
      setLoading(true);
      setLoadError('');

      const templateData =
        await clinicalTemplateAdminService.getAllPalpationTemplates(
          bodyPartTemplateId
        );

      setTemplates(Array.isArray(templateData) ? templateData : []);
    } catch (error) {
      setTemplates([]);
      setLoadError(
        error?.message ||
          'Palpation template configuration could not be loaded.'
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setForm({
      ...EMPTY_FORM,
      bodyPartTemplateId: selectedBodyPartId || '',
    });
    setFormErrors({});
    setActionError('');
  };

  const beginEdit = (template) => {
    setEditingTemplate(template);
    setForm(normalizeTemplateToForm(template));
    setSelectedBodyPartId(String(template.bodyPartTemplateId));
    setFormErrors({});
    setActionError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateFormField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));

    setFormErrors((current) => {
      if (!current[field]) {
        return current;
      }

      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const handleFormBodyPartChange = (value) => {
    const bodyPart = bodyPartMap.get(Number(value));

    setForm((current) => ({
      ...current,
      bodyPartTemplateId: value,
      sideSpecific:
        current.sideSpecific && bodyPart && !bodyPart.supportsLaterality
          ? false
          : current.sideSpecific,
      templateLaterality:
        current.sideSpecific && bodyPart && !bodyPart.supportsLaterality
          ? ''
          : current.templateLaterality,
      active:
        current.active && bodyPart && !bodyPart.active ? false : current.active,
    }));

    setSelectedBodyPartId(value);
    setFormErrors({});
    setActionError('');
  };

  const submitForm = async (event) => {
    event.preventDefault();

    const errors = validateForm(form, bodyPartMap);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      setActionError('Correct the highlighted fields before saving.');
      return;
    }

    try {
      setSaving(true);
      setActionError('');

      const request = buildRequest(form);

      if (isEditing) {
        await clinicalTemplateAdminService.updatePalpationTemplate(
          editingTemplate.palpationTemplateId,
          request
        );
        showSuccess('Palpation template updated.');
      } else {
        await clinicalTemplateAdminService.createPalpationTemplate(request);
        showSuccess('Palpation template created.');
      }

      const bodyPartId = String(request.bodyPartTemplateId);
      setSelectedBodyPartId(bodyPartId);
      setEditingTemplate(null);
      setForm({
        ...EMPTY_FORM,
        bodyPartTemplateId: bodyPartId,
      });
      setFormErrors({});
      setActionError('');
      await loadTemplates(bodyPartId);
    } catch (error) {
      setActionError(
        error?.message || 'Palpation template could not be saved.'
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (template) => {
    const nextActive = !template.active;
    const bodyPart = bodyPartMap.get(Number(template.bodyPartTemplateId));

    if (nextActive && bodyPart && !bodyPart.active) {
      setActionError(
        `"${bodyPart.displayName}" is inactive. Activate the body part before activating this palpation template.`
      );
      return;
    }

    if (!nextActive) {
      const confirmed = window.confirm(
        `Deactivate "${template.displayName}"? Historical clinical notes will keep their existing palpation template reference.`
      );

      if (!confirmed) {
        return;
      }
    }

    try {
      setActiveActionId(template.palpationTemplateId);
      setActionError('');

      await clinicalTemplateAdminService.setPalpationTemplateActive(
        template.palpationTemplateId,
        nextActive
      );

      showSuccess(
        `Palpation template ${nextActive ? 'activated' : 'deactivated'}.`
      );

      if (
        editingTemplate?.palpationTemplateId ===
        template.palpationTemplateId
      ) {
        setForm((current) => ({
          ...current,
          active: nextActive,
        }));
        setEditingTemplate((current) => ({
          ...current,
          active: nextActive,
        }));
      }

      await loadTemplates(template.bodyPartTemplateId);
    } catch (error) {
      setActionError(
        error?.message ||
          `Palpation template could not be ${nextActive ? 'activated' : 'deactivated'}.`
      );
    } finally {
      setActiveActionId(null);
    }
  };

  return (
    <div className="palpation-template-admin">
      <section className="clinical-template-editor-panel">
        <div className="clinical-template-editor-heading clinical-template-editor-heading-row">
          <div>
            <span className="clinical-template-eyebrow">
              PALPATION CONFIGURATION
            </span>
            <h2>
              {isEditing
                ? 'Edit Palpation Template'
                : 'Create Palpation Template'}
            </h2>
            <p>
              Configure reusable palpation findings for one body part. Side-specific
              items can generate separate LEFT and RIGHT documentation when
              laterality applies.
            </p>
          </div>

          {isEditing && (
            <button
              type="button"
              className="clinical-template-secondary-button"
              onClick={resetForm}
              disabled={saving}
            >
              Cancel Edit
            </button>
          )}
        </div>

        {actionError && (
          <div className="clinical-template-message error" role="alert">
            {actionError}
          </div>
        )}

        {success && (
          <div className="clinical-template-message success" role="status">
            {success}
          </div>
        )}

        <form className="palpation-template-form" onSubmit={submitForm}>
          <div className="clinical-template-form-grid">
            <label>
              Body Part
              <select
                value={form.bodyPartTemplateId}
                onChange={(event) =>
                  handleFormBodyPartChange(event.target.value)
                }
                disabled={isEditing || saving}
              >
                <option value="">Select body part</option>
                {bodyParts.map((bodyPart) => (
                  <option
                    key={bodyPart.bodyPartTemplateId}
                    value={bodyPart.bodyPartTemplateId}
                  >
                    {bodyPart.displayName} ({bodyPart.bodyPartCode})
                    {!bodyPart.active ? ' - Inactive' : ''}
                  </option>
                ))}
              </select>
              {formErrors.bodyPartTemplateId && (
                <span className="clinical-template-field-error">
                  {formErrors.bodyPartTemplateId}
                </span>
              )}
              {isEditing && (
                <small>Body-part assignment cannot be changed after creation.</small>
              )}
            </label>

            <label>
              Palpation Code
              <input
                type="text"
                value={form.palpationCode}
                onChange={(event) =>
                  updateFormField('palpationCode', event.target.value.toUpperCase())
                }
                maxLength={120}
                disabled={isEditing || saving}
                placeholder="PLANTAR_FASCIA"
              />
              {formErrors.palpationCode && (
                <span className="clinical-template-field-error">
                  {formErrors.palpationCode}
                </span>
              )}
              {isEditing && (
                <small>Palpation code cannot be changed after creation.</small>
              )}
            </label>

            <label>
              Display Name
              <input
                type="text"
                value={form.displayName}
                onChange={(event) =>
                  updateFormField('displayName', event.target.value)
                }
                maxLength={150}
                disabled={saving}
                placeholder="Plantar Fascia"
              />
              {formErrors.displayName && (
                <span className="clinical-template-field-error">
                  {formErrors.displayName}
                </span>
              )}
            </label>

            <label>
              Display Order
              <input
                type="number"
                min="0"
                step="1"
                value={form.displayOrder}
                onChange={(event) =>
                  updateFormField('displayOrder', event.target.value)
                }
                disabled={saving}
              />
              {formErrors.displayOrder && (
                <span className="clinical-template-field-error">
                  {formErrors.displayOrder}
                </span>
              )}
            </label>


            {form.sideSpecific && (
              <label>
                Template Side
                <select
                  value={form.templateLaterality}
                  onChange={(event) =>
                    updateFormField('templateLaterality', event.target.value)
                  }
                  disabled={saving}
                >
                  <option value="">Select side</option>
                  <option value="LEFT">Left</option>
                  <option value="RIGHT">Right</option>
                </select>
                {formErrors.templateLaterality && (
                  <span className="clinical-template-field-error">
                    {formErrors.templateLaterality}
                  </span>
                )}
                <small>Only notes matching this side will use this template. Bilateral notes can use both Left and Right templates.</small>
              </label>
            )}
          </div>

          <div className="clinical-template-checkbox-grid">
            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.sideSpecific}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setForm((current) => ({
                    ...current,
                    sideSpecific: checked,
                    templateLaterality: checked
                      ? current.templateLaterality
                      : '',
                  }));
                  setFormErrors((current) => {
                    const next = { ...current };
                    delete next.sideSpecific;
                    delete next.templateLaterality;
                    return next;
                  });
                }}
                disabled={
                  saving ||
                  (Boolean(form.bodyPartTemplateId) &&
                    selectedBodyPart &&
                    !selectedBodyPart.supportsLaterality)
                }
              />
              <span>
                <strong>Side Specific</strong>
                <small>Assign this template to one concrete side.</small>
                {formErrors.sideSpecific && (
                  <span className="clinical-template-field-error">
                    {formErrors.sideSpecific}
                  </span>
                )}
              </span>
            </label>

            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.required}
                onChange={(event) =>
                  updateFormField('required', event.target.checked)
                }
                disabled={saving}
              />
              <span>
                <strong>Required</strong>
                <small>
                  This palpation item must be completed before final validation.
                </small>
              </span>
            </label>

            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(event) =>
                  updateFormField('active', event.target.checked)
                }
                disabled={saving}
              />
              <span>
                <strong>Active</strong>
                <small>Available for newly created clinical notes.</small>
                {formErrors.active && (
                  <span className="clinical-template-field-error">
                    {formErrors.active}
                  </span>
                )}
              </span>
            </label>
          </div>

          <label className="clinical-template-full-width-field">
            Admin Notes
            <textarea
              value={form.adminNotes}
              onChange={(event) =>
                updateFormField('adminNotes', event.target.value)
              }
              maxLength={500}
              disabled={saving}
              placeholder="Optional configuration notes..."
            />
            {formErrors.adminNotes && (
              <span className="clinical-template-field-error">
                {formErrors.adminNotes}
              </span>
            )}
          </label>

          <div className="clinical-template-form-actions">
            <button type="submit" disabled={saving}>
              {saving
                ? 'Saving...'
                : isEditing
                  ? 'Save Palpation Template'
                  : 'Create Palpation Template'}
            </button>

            {isEditing && (
              <button
                type="button"
                className="clinical-template-secondary-button"
                onClick={resetForm}
                disabled={saving}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </section>

      <section className="clinical-template-editor-panel clinical-template-list-panel">
        <div className="clinical-template-editor-heading clinical-template-editor-heading-row">
          <div>
            <span className="clinical-template-eyebrow">
              CONFIGURED PALPATION TEMPLATES
            </span>
            <h2>Palpation Templates</h2>
            <p>
              {selectedBodyPart
                ? `${selectedBodyPart.displayName}: ${templates.length} total · ${activeCount} active`
                : 'Select a body part to view its palpation configuration.'}
            </p>
          </div>

          <button
            type="button"
            className="clinical-template-secondary-button"
            onClick={() =>
              selectedBodyPartId && loadTemplates(selectedBodyPartId)
            }
            disabled={
              !selectedBodyPartId ||
              loading ||
              saving ||
              activeActionId !== null
            }
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {loadError && (
          <div className="clinical-template-message error" role="alert">
            {loadError}
          </div>
        )}

        <div
          className="clinical-template-list-toolbar"
          style={{ gridTemplateColumns: '260px minmax(220px, 1fr) 220px' }}
        >
          <select
            value={selectedBodyPartId}
            onChange={(event) => {
              setSelectedBodyPartId(event.target.value);
              if (!isEditing) {
                setForm((current) => ({
                  ...current,
                  bodyPartTemplateId: event.target.value,
                }));
              }
              setActionError('');
            }}
          >
            <option value="">Select body part</option>
            {bodyParts.map((bodyPart) => (
              <option
                key={bodyPart.bodyPartTemplateId}
                value={bodyPart.bodyPartTemplateId}
              >
                {bodyPart.displayName} ({bodyPart.bodyPartCode})
                {!bodyPart.active ? ' - Inactive' : ''}
              </option>
            ))}
          </select>

          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search palpation code, name, notes..."
            disabled={!selectedBodyPartId}
          />

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            disabled={!selectedBodyPartId}
          >
            <option value="ALL">All statuses</option>
            <option value="ACTIVE">Active only</option>
            <option value="INACTIVE">Inactive only</option>
          </select>
        </div>

        {!selectedBodyPartId ? (
          <div className="clinical-template-empty-state">
            Select a body part to manage its palpation templates.
          </div>
        ) : loading ? (
          <div className="clinical-template-empty-state">
            Loading palpation templates...
          </div>
        ) : sortedTemplates.length === 0 ? (
          <div className="clinical-template-empty-state">
            No palpation templates match the current filter.
          </div>
        ) : (
          <div className="clinical-template-table-wrap">
            <table className="clinical-template-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Palpation Item</th>
                  <th>Side</th>
                  <th>Required</th>
                  <th>Status</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedTemplates.map((template) => (
                  <tr key={template.palpationTemplateId}>
                    <td>{template.displayOrder}</td>
                    <td>
                      <strong>{template.displayName}</strong>
                      <small className="clinical-template-table-subtext">
                        {template.palpationCode}
                      </small>
                      {template.adminNotes && (
                        <small className="clinical-template-table-subtext">
                          {template.adminNotes}
                        </small>
                      )}
                    </td>
                    <td>{template.sideSpecific ? template.templateLaterality || 'Needs side' : 'N/A'}</td>
                    <td>{template.required ? 'Yes' : 'No'}</td>
                    <td>
                      <span
                        className={`clinical-template-status ${
                          template.active ? 'active' : 'inactive'
                        }`}
                      >
                        {template.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {formatDateTime(template.updatedAt)}
                      <small className="clinical-template-table-subtext">
                        Staff {template.updatedByStaffId ?? 'N/A'}
                      </small>
                    </td>
                    <td>
                      <div className="clinical-template-row-actions">
                        <button
                          type="button"
                          className="clinical-template-secondary-button"
                          onClick={() => beginEdit(template)}
                          disabled={saving || activeActionId !== null}
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          className={
                            template.active
                              ? 'clinical-template-danger-button'
                              : 'clinical-template-success-button'
                          }
                          onClick={() => toggleActive(template)}
                          disabled={
                            saving ||
                            activeActionId === template.palpationTemplateId
                          }
                        >
                          {activeActionId === template.palpationTemplateId
                            ? 'Updating...'
                            : template.active
                              ? 'Deactivate'
                              : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}